import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaWhatsapp } from 'react-icons/fa';
import { authService } from '../services/authService';
import { setCredentials } from '../redux/slices/authSlice';

// Máscara BR: (XX) XXXXX-XXXX
const formatWhatsapp = (value) => {
  const d = (value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/**
 * Popup global: pede o WhatsApp de usuários logados que ainda não têm o número
 * (contas criadas antes do WhatsApp virar obrigatório no cadastro).
 */
export default function WhatsappPrompt() {
  const token = useSelector((s) => s.auth.token);
  const user = useSelector((s) => s.auth.user);
  const dispatch = useDispatch();

  const [value, setValue] = useState(''); // dígitos
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const needsWhatsapp = Boolean(token && user && !user.whatsapp);
  if (!needsWhatsapp || dismissed) return null;

  const handleSave = async () => {
    setError(null);
    if (value.length < 10 || value.length > 11) {
      setError('Informe um WhatsApp válido com DDD (ex.: (11) 91234-5678).');
      return;
    }
    setSubmitting(true);
    try {
      await authService.updateWhatsapp(token, value);
      // Atualiza o usuário no Redux/localStorage → o popup some.
      dispatch(setCredentials({ token, user: { ...user, whatsapp: value } }));
    } catch (err) {
      setError(err?.message || 'Não foi possível salvar agora. Tente novamente.');
      setSubmitting(false);
    }
  };

  // Salva o número ASSIM QUE digitado (ao sair do campo), antes mesmo de clicar
  // em "Salvar". Garante que o telefone seja persistido e o usuário apareça nas
  // estatísticas, mesmo que feche o popup sem confirmar. Best-effort e silencioso.
  const handleBlur = async () => {
    if (submitting) return;
    if (value.length < 10 || value.length > 11) return;
    try {
      await authService.updateWhatsapp(token, value);
      dispatch(setCredentials({ token, user: { ...user, whatsapp: value } }));
    } catch (err) {
      // Silencioso: o botão "Salvar" continua disponível para nova tentativa.
      console.warn('[WhatsappPrompt] Falha ao salvar no blur:', err?.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#141414',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          borderRadius: 14,
          padding: '28px 24px',
          textAlign: 'center',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 16px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(37, 211, 102, 0.12)',
            border: '2px solid #25D366',
            color: '#25D366',
            fontSize: 32,
          }}
        >
          <FaWhatsapp />
        </div>

        <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
          Complete seu cadastro
        </h2>
        <p style={{ color: '#cfcfcf', fontSize: 14, lineHeight: 1.5, margin: '0 0 18px' }}>
          Informe seu WhatsApp para receber suporte e novidades sobre suas compras.
        </p>

        <input
          type="tel"
          inputMode="numeric"
          placeholder="(11) 91234-5678"
          value={formatWhatsapp(value)}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 11))}
          onBlur={handleBlur}
          disabled={submitting}
          autoFocus
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 16px',
            marginBottom: error ? 8 : 16,
            background: 'rgba(255, 255, 255, 0.05)',
            border: '2px solid rgba(255, 215, 0, 0.5)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 16,
            textAlign: 'center',
          }}
        />

        {error && (
          <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '14px',
            background: '#ffd700',
            color: '#1a1a1a',
            border: 'none',
            borderRadius: 8,
            fontWeight: 800,
            fontSize: 15,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            marginBottom: 8,
          }}
        >
          {submitting ? 'Salvando…' : 'Salvar WhatsApp'}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '8px',
            background: 'transparent',
            color: '#9aa0a6',
            border: 'none',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Agora não
        </button>
      </div>
    </div>
  );
}
